/**
 * 从一张或多张图片 URL（object URL / data URL）采样主色，用于简易色板展示。
 */
function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

type Bucket = { sr: number; sg: number; sb: number; n: number };

function sampleOneImage(src: string): Promise<Map<string, Bucket>> {
  return new Promise((resolve) => {
    const img = new Image();
    const done = (map: Map<string, Bucket>) => resolve(map);
    img.onload = () => {
      const w = 48;
      const h = 48;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        done(new Map());
        return;
      }
      try {
        ctx.drawImage(img, 0, 0, w, h);
      } catch {
        done(new Map());
        return;
      }
      let data: ImageData;
      try {
        data = ctx.getImageData(0, 0, w, h);
      } catch {
        done(new Map());
        return;
      }
      const q = 28;
      const buckets = new Map<string, Bucket>();
      for (let i = 0; i < data.data.length; i += 4) {
        if (data.data[i + 3] < 20) continue;
        const r = data.data[i];
        const g = data.data[i + 1];
        const b = data.data[i + 2];

        // Saturation weighting: 强调饱和色（蓝/青/红）而不是大量低饱和中性灰
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const sat = max === 0 ? 0 : (max - min) / max; // 0~1
        if (sat < 0.12) continue;

        // Weight 越高，越能影响 bucket 代表色
        const w = 1 + sat * 3;

        const key = `${Math.floor(r / q)},${Math.floor(g / q)},${Math.floor(b / q)}`;
        const p = buckets.get(key);
        if (p) {
          p.sr += r * w;
          p.sg += g * w;
          p.sb += b * w;
          p.n += w;
        } else {
          buckets.set(key, { sr: r * w, sg: g * w, sb: b * w, n: w });
        }
      }
      done(buckets);
    };
    img.onerror = () => done(new Map());
    img.src = src;
  });
}

/** 合并多张图的主色，返回最多 maxColors 个 HEX */
export async function extractPaletteFromImageUrls(urls: string[], maxColors = 10): Promise<string[]> {
  const valid = urls.filter(Boolean);
  if (valid.length === 0) return [];

  const merged = new Map<string, Bucket>();

  for (const src of valid) {
    try {
      const part = await sampleOneImage(src);
      for (const [key, v] of part) {
        const prev = merged.get(key);
        if (prev) {
          prev.sr += v.sr;
          prev.sg += v.sg;
          prev.sb += v.sb;
          prev.n += v.n;
        } else {
          merged.set(key, { sr: v.sr, sg: v.sg, sb: v.sb, n: v.n });
        }
      }
    } catch {
      /* skip */
    }
  }

  const list = [...merged.values()]
    .map((v) => ({
      r: v.sr / v.n,
      g: v.sg / v.n,
      b: v.sb / v.n,
      w: v.n,
    }))
    .sort((a, b) => b.w - a.w);

  const toHex = (c: { r: number; g: number; b: number }) => rgbToHex(c.r, c.g, c.b);
  const isBlueish = (c: { r: number; g: number; b: number }) => c.b >= c.r + 18 && c.b >= c.g + 8;

  // 先按权重取 top
  let picked = list.slice(0, maxColors);

  // 若没有明显蓝/青家族颜色，则强制挑一个“最蓝”的 bucket 替换掉最后一个，保证蓝色可被 DNA 注入
  if (!picked.some((c) => isBlueish(c)) && list.length > 0) {
    const bestBlue = list.reduce((best, c) => {
      const bestScore = best.b - Math.max(best.r, best.g);
      const score = c.b - Math.max(c.r, c.g);
      return score > bestScore ? c : best;
    }, list[0]);
    if (picked.length > 0) {
      picked = [...picked.slice(0, Math.max(0, picked.length - 1)), bestBlue];
    } else {
      picked = [bestBlue];
    }
  }

  return picked.map((c) => toHex(c));
}
